// Client-side friend system service

export interface FriendInfo {
  playerId: string;
  name: string;
  friendCode: string;
  status: 'pending' | 'accepted' | 'blocked';
  online: boolean;
  inMatch: boolean;
  roomId?: string;
  elo?: number;
}

export interface PendingRequest {
  playerId: string;
  name: string;
  friendCode: string;
}

export class FriendClient {
  private workerUrl: string;
  private playerId: string;
  private playerName: string;
  private friendCode: string | null = null;
  private heartbeatInterval: number | null = null;
  private currentRoomId: string | null = null;

  constructor(playerId: string, playerName: string) {
    const isDev =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';
    this.workerUrl = isDev
      ? 'http://localhost:8788'
      : 'https://wizard-duel-api.neenhouse.workers.dev';
    this.playerId = playerId;
    this.playerName = playerName;
  }

  async register(): Promise<string> {
    const res = await fetch(`${this.workerUrl}/api/friends/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId: this.playerId, name: this.playerName }),
    });
    const data = await res.json() as { friendCode: string };
    this.friendCode = data.friendCode;
    return data.friendCode;
  }

  async getFriendCode(): Promise<string> {
    if (this.friendCode) return this.friendCode;

    const res = await fetch(
      `${this.workerUrl}/api/friends/code?playerId=${encodeURIComponent(this.playerId)}`,
    );
    const data = await res.json() as { friendCode: string };
    this.friendCode = data.friendCode;
    return data.friendCode;
  }

  async sendFriendRequest(friendCode: string): Promise<{ status: string; friendName?: string; error?: string }> {
    const res = await fetch(`${this.workerUrl}/api/friends/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId: this.playerId, friendCode }),
    });
    return res.json() as Promise<{ status: string; friendName?: string; error?: string }>;
  }

  async acceptRequest(friendId: string): Promise<void> {
    await fetch(`${this.workerUrl}/api/friends/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId: this.playerId, friendId }),
    });
  }

  async removeFriend(friendId: string): Promise<void> {
    await fetch(`${this.workerUrl}/api/friends/remove`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId: this.playerId, friendId }),
    });
  }

  async listFriends(): Promise<FriendInfo[]> {
    const res = await fetch(
      `${this.workerUrl}/api/friends?playerId=${encodeURIComponent(this.playerId)}`,
    );
    const data = await res.json() as { friends: FriendInfo[] };
    return data.friends;
  }

  async getPendingRequests(): Promise<PendingRequest[]> {
    const res = await fetch(
      `${this.workerUrl}/api/friends/pending?playerId=${encodeURIComponent(this.playerId)}`,
    );
    const data = await res.json() as { pending: PendingRequest[] };
    return data.pending;
  }

  async getOnlineStatuses(): Promise<Array<{ playerId: string; name: string; online: boolean; inMatch: boolean; roomId?: string }>> {
    const res = await fetch(
      `${this.workerUrl}/api/friends/online?playerId=${encodeURIComponent(this.playerId)}`,
    );
    const data = await res.json() as { statuses: Array<{ playerId: string; name: string; online: boolean; inMatch: boolean; roomId?: string }> };
    return data.statuses;
  }

  // Start sending heartbeats to maintain online status
  startHeartbeat(roomId?: string): void {
    this.currentRoomId = roomId || null;
    this.sendHeartbeat();

    // Send heartbeat every 30 seconds
    this.heartbeatInterval = window.setInterval(() => {
      this.sendHeartbeat();
    }, 30000);
  }

  stopHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    // Notify offline
    fetch(`${this.workerUrl}/api/friends/offline`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId: this.playerId }),
    }).catch(() => {});
  }

  updateRoomId(roomId: string | null): void {
    this.currentRoomId = roomId;
    this.sendHeartbeat();
  }

  private sendHeartbeat(): void {
    fetch(`${this.workerUrl}/api/friends/heartbeat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        playerId: this.playerId,
        name: this.playerName,
        roomId: this.currentRoomId,
      }),
    }).catch(() => {});
  }

  dispose(): void {
    this.stopHeartbeat();
  }
}
